// Example: CustomerDetailPage.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  Button,
  IconButton,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  Home as HomeIcon,
  Notes as NotesIcon,
  Cloud as CloudIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  GetApp as DownloadIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getCustomerById, deleteCustomer } from '../services/customerService';
import { getPaymentsByCustomerId } from '../../payments/services/paymentService';
import { getInvoicesByCustomer } from '../../invoices/services/invoiceService';
import { getTicketsByCustomerId } from '../../ticketdecaisse/services/TicketDeCaisseService';
import { useTranslation } from "react-i18next";

// Helper for tab panels
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-tabpanel-${index}`}
      aria-labelledby={`customer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const navigationData = location.state?.customerData;
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(!navigationData);

  // Tab state for all your tabs
  const [tabIndex, setTabIndex] = useState(0);

  // Payments state
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState(null);

  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState(null);

  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState(null);

  useEffect(() => {
    if (navigationData) {
      setCustomer(navigationData);
      setLoading(false);
    } else {
      loadCustomerFromAPI();
    }
    // eslint-disable-next-line
  }, [id, navigationData]);

  // Fetch payments when payments tab is selected
  useEffect(() => {
    if (tabIndex === 3 && id) {
      fetchPayments();
    }
  }, [tabIndex, id]);

  // Fetch invoices when invoices tab is selected
  useEffect(() => {
    if (tabIndex === 2 && id) {
      fetchInvoices();
    }
  }, [tabIndex, id]);

  // Fetch tickets when tickets tab is selected (tab index 5)
  useEffect(() => {
    if (tabIndex === 5 && id) {
      fetchTickets();
    }
  }, [tabIndex, id]);

  const loadCustomerFromAPI = async () => {
    try {
      setLoading(true);
      const response = await getCustomerById(id);
      const customerData = response?.data?.payload || response?.payload || response?.data || response;
      if (customerData) {
        setCustomer(customerData);
      } else {
        throw new Error('Customer not found');
      }
    } catch (error) {
      toast.error(t("customer_not_found"));
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      setPaymentsLoading(true);
      setPaymentsError(null);
      console.log('🔍 Fetching payments for customer ID:', id);
      
      const response = await getPaymentsByCustomerId(id);
      
      console.log('📦 Raw payments response:', response);
      
      // Handle different response structures
      let paymentsData = [];
      if (response?.payload && Array.isArray(response.payload)) {
        paymentsData = response.payload;
      } else if (response?.data?.payload && Array.isArray(response.data.payload)) {
        paymentsData = response.data.payload;
      } else if (Array.isArray(response?.data)) {
        paymentsData = response.data;
      } else if (Array.isArray(response)) {
        paymentsData = response;
      }
      
      console.log('✅ Fetched payments:', paymentsData.length, 'payments');
      setPayments(paymentsData);
    } catch (error) {
      console.error('❌ Error fetching customer payments:', error);
      setPaymentsError(error.message || 'Failed to load payments');
      toast.error(t("failed_to_load_payments") || 'Failed to load payments');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      setInvoicesLoading(true);
      setInvoicesError(null);
      console.log('🔍 Fetching invoices for customer ID:', id);
      
      const response = await getInvoicesByCustomer(id);
      
      console.log('📦 Raw invoices response:', response);
      
      // Handle different response structures
      let invoicesData = [];
      if (response?.payload && Array.isArray(response.payload)) {
        invoicesData = response.payload;
      } else if (response?.data?.payload && Array.isArray(response.data.payload)) {
        invoicesData = response.data.payload;
      } else if (Array.isArray(response?.data)) {
        invoicesData = response.data;
      } else if (Array.isArray(response)) {
        invoicesData = response;
      }
      
      console.log('✅ Fetched invoices:', invoicesData.length, 'invoices');
      setInvoices(invoicesData);
    } catch (error) {
      console.error('❌ Error fetching customer invoices:', error);
      setInvoicesError(error.message || 'Failed to load invoices');
      toast.error(t("failed_to_load_invoices") || 'Failed to load invoices');
    } finally {
      setInvoicesLoading(false);
    }
  };

  const fetchTickets = async () => {
    try {
      setTicketsLoading(true);
      setTicketsError(null);
      console.log('🔍 Fetching tickets for customer ID:', id);
      
      const response = await getTicketsByCustomerId(id);
      
      console.log('📦 Raw tickets response:', response);
      
      // Handle different response structures
      let ticketsData = [];
      if (response?.payload && Array.isArray(response.payload)) {
        ticketsData = response.payload;
      } else if (response?.data?.payload && Array.isArray(response.data.payload)) {
        ticketsData = response.data.payload;
      } else if (Array.isArray(response?.data)) {
        ticketsData = response.data;
      } else if (Array.isArray(response)) {
        ticketsData = response;
      }
      
      console.log('✅ Fetched tickets:', ticketsData.length, 'tickets');
      setTickets(ticketsData);
    } catch (error) {
      console.error('❌ Error fetching customer tickets:', error);
      setTicketsError(error.message || 'Failed to load tickets');
      toast.error(t("failed_to_load_tickets") || 'Failed to load tickets');
    } finally {
      setTicketsLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/customers/edit/${id}`, {
      state: { customerData: customer }
    });
  };

  const handleDelete = async () => {
    const customerName = customer?.CustomerName || customer?.Name || t("customer");
    if (window.confirm(t("confirm_delete_customer", { customer: customerName }))) {
      try {
        await deleteCustomer(id);
        toast.success(t("customer_deleted_success"));
        navigate('/customers');
      } catch (error) {
        if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error(t("failed_to_delete_customer"));
        }
      }
    }
  };

  const handleCreateInvoice = () => {
    navigate('/invoices/add', {
      state: {
        prefilledCustomer: {
          value: customer.Id,
          label: customer.CustomerName || customer.Name,
          Id: customer.Id,
          Name: customer.CustomerName || customer.Name,
          Email: customer.Email || customer.email,
          Phone: customer.PhoneNumber || customer.Phone,
          VAT: customer.TaxNumber || customer.VATNumber || customer.CustomerVAT,
          Address: customer.Address || [
            customer.Street,
            customer.Box,
            customer.PostCode,
            customer.City,
            customer.Country
          ].filter(Boolean).join(', '),
          Street: customer.Street,
          StreetNumber: customer.Box,
          City: customer.City,
          PostalCode: customer.PostCode,
          Country: customer.Country,
          CustomerPeppolId: customer.CustomerPeppolId
        }
      }
    });
  };

  const handleViewInvoice = (invoiceId) => {
    navigate(`/invoices/view/${invoiceId}`);
  };

  const getPaymentMethodColor = (method) => {
    const colors = {
      CASH: 'success',
      BANCONTACT: 'primary',
      VISA: 'info',
      MASTERCARD: 'warning',
      CHEQUE: 'secondary',
      VIREMENT: 'default',
      PREPAID: 'info',
    };
    return colors[method?.toUpperCase()] || 'default';
  };

  const getSourceTypeColor = (sourceType) => {
    const colors = {
      Invoice: 'primary',
      Offer: 'warning',
      Ticket: 'success',
    };
    return colors[sourceType] || 'default';
  };

  const getInvoiceStatusColor = (status) => {
    const colors = {
      'Paid': 'success',
      'Partially Paid': 'warning',
      'Unpaid': 'error',
      'Overdue': 'error',
      'Draft': 'default',
      'Cancelled': 'default',
    };
    return colors[status] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!customer) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography color="error">{t("customer_not_found")}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/customers')} sx={{ color: 'primary.main' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {customer.CustomerName || customer.Name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {(customer.Email || customer.email || t("no_email")) + " • " + t("customer_details")}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{ borderRadius: 2 }}
          >
            {t("edit")}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            sx={{ borderRadius: 2 }}
          >
            {t("delete")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateInvoice}
            sx={{ borderRadius: 2 }}
          >
            {t("create_invoice")}
          </Button>
        </Box>
      </Box>
      
      {/* Tabs and Tab Panels */}
      <Paper sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabIndex}
            onChange={(e, v) => setTabIndex(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 3 }}
          >
            <Tab label={t("customer_info_tab") || "Customer Info"} />
            <Tab label={t("proposals_tab") || "Proposals"} />
            <Tab label={t("invoices_tab") || "Invoices"} />
            <Tab label={t("payments_tab") || "Payments"} />
            <Tab label={t("credit_notes_tab") || "Credit Notes"} />
            <Tab label={t("tickets_tab") || "Tickets de Caisse"} />
            <Tab label={t("documents_tab") || "Documents"} />
            <Tab label={t("other_tab") || "Other"} />
          </Tabs>
        </Box>
        <Divider sx={{ mb: 1 }} />

        {/* Customer Info Tab */}
        <TabPanel value={tabIndex} index={0}>
          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t("basic_information")}
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("customer_name")}
                    secondary={customer.CustomerName || customer.Name || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("email")}
                    secondary={customer.Email || customer.email || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("phone")}
                    secondary={customer.PhoneNumber || customer.Phone || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <BusinessIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("customer_type")}
                    secondary={customer.CustomerType || 'N/A'}
                  />
                </ListItem>
                {/* ✅ Peppol ID Field */}
                <ListItem>
                  <ListItemIcon>
                    <CloudIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("peppol_id")}
                    secondary={
                      customer.CustomerPeppolId ? (
                        <Chip 
                          label={customer.CustomerPeppolId} 
                          size="small" 
                          color="info"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t("not_configured")}
                        </Typography>
                      )
                    }
                  />
                </ListItem>
              </List>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t("company_address")}
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <BusinessIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("company_name")}
                    secondary={customer.CompanyName || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <BusinessIcon color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("tax_number")}
                    secondary={customer.TaxNumber || customer.VATNumber || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <HomeIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("address")}
                    secondary={
                      [
                        customer.Street,
                        customer.Box,
                        customer.PostCode,
                        customer.City,
                        customer.Country
                      ].filter(Boolean).join(', ') || customer.Address || 'N/A'
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LanguageIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("website")}
                    secondary={customer.Website || 'N/A'}
                  />
                </ListItem>
              </List>
            </Grid>

            {/* Notes Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {t("notes")}
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <NotesIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={customer.Notes || t("no_notes")}
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Proposals Tab */}
        <TabPanel value={tabIndex} index={1}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("proposals_tab") || "Proposals"}
          </Typography>
          <Alert severity="info">
            {t("proposals_coming_soon") || "Proposals content will be displayed here."}
          </Alert>
        </TabPanel>

        {/* Invoices Tab */}
        <TabPanel value={tabIndex} index={2}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t("invoices_tab") || "Customer Invoices"}
          </Typography>
          
          {invoicesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <CircularProgress />
            </Box>
          ) : invoicesError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {invoicesError}
            </Alert>
          ) : invoices.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#f5f5f5' }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {t("no_invoices_found") || "No invoices found for this customer."}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateInvoice}
                sx={{ mt: 2 }}
              >
                {t("create_first_invoice") || "Create First Invoice"}
              </Button>
            </Paper>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("invoice_number") || "Invoice #"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("items") || "Items"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }} align="right">
                        {t("total_amount") || "Total Amount"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }} align="right">
                        {t("paid_amount") || "Paid Amount"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }} align="right">
                        {t("due_amount") || "Due Amount"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("status") || "Status"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("vat") || "VAT %"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }} align="center">
                        {t("actions") || "Actions"}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((invoice) => {
                      const totalItems = invoice.LineItems?.length || 0;
                      const totalQuantity = invoice.LineItems?.reduce((sum, item) => sum + (item.Quantity || 0), 0) || 0;
                      
                      return (
                        <TableRow 
                          key={invoice.Id}
                          sx={{ 
                            '&:hover': { bgcolor: '#f9f9f9', cursor: 'pointer' },
                            '&:last-child td, &:last-child th': { border: 0 }
                          }}
                          onClick={() => handleViewInvoice(invoice.Id)}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                              {invoice.InvoiceNumber || '-'}
                            </Typography>
                            {invoice.CustomText && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {invoice.CustomText}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {totalItems} {totalItems === 1 ? 'item' : 'items'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Qty: {totalQuantity}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatCurrency(invoice.InvoicePrice || 0)}
                            </Typography>
                            {invoice.Discount > 0 && (
                              <Chip
                                label={`-${formatCurrency(invoice.Discount)}`}
                                size="small"
                                color="warning"
                                sx={{ mt: 0.5, fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                              {formatCurrency(invoice.InvoicePaidAmount || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 700,
                                color: (invoice.InvoiceDueAmount || 0) > 0 ? 'error.main' : 'success.main'
                              }}
                            >
                              {formatCurrency(invoice.InvoiceDueAmount || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={invoice.Status || 'Unknown'}
                              size="small"
                              color={getInvoiceStatusColor(invoice.Status)}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${invoice.VATPercentage || 0}%`}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewInvoice(invoice.Id)}
                              title={t("view_invoice") || "View Invoice"}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Invoice Summary */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={3}>
                  <Paper sx={{ p: 2, bgcolor: '#e3f2fd', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t("total_invoices") || "Total Invoices"}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {invoices.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {invoices.reduce((sum, inv) => sum + (inv.LineItems?.length || 0), 0)} total items
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Paper sx={{ p: 2, bgcolor: '#f3e5f5', borderLeft: '4px solid', borderColor: 'secondary.main' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t("total_amount") || "Total Invoice Amount"}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                      {formatCurrency(invoices.reduce((sum, inv) => sum + (inv.InvoicePrice || 0), 0))}
                    </Typography>
                    {invoices.some(inv => inv.Discount > 0) && (
                      <Typography variant="caption" color="text.secondary">
                        Discount: {formatCurrency(invoices.reduce((sum, inv) => sum + (inv.Discount || 0), 0))}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Paper sx={{ p: 2, bgcolor: '#e8f5e9', borderLeft: '4px solid', borderColor: 'success.main' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t("paid_amount") || "Total Paid"}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {formatCurrency(invoices.reduce((sum, inv) => sum + (inv.InvoicePaidAmount || 0), 0))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {invoices.filter(inv => inv.Status === 'Paid').length} paid invoices
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Paper sx={{ p: 2, bgcolor: '#ffebee', borderLeft: '4px solid', borderColor: 'error.main' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t("outstanding_balance") || "Total Due"}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
                      {formatCurrency(invoices.reduce((sum, inv) => sum + (inv.InvoiceDueAmount || 0), 0))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {invoices.filter(inv => (inv.InvoiceDueAmount || 0) > 0).length} unpaid invoices
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Line Items Summary (Optional Detail View) */}
              <Paper sx={{ mt: 2, p: 2, bgcolor: '#fafafa' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t("invoice_breakdown") || "Invoice Breakdown"}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Total Products
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {invoices.reduce((sum, inv) => sum + (inv.LineItems?.length || 0), 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Total Quantity
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {invoices.reduce((sum, inv) => 
                        sum + (inv.LineItems?.reduce((itemSum, item) => itemSum + (item.Quantity || 0), 0) || 0), 0
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Avg Invoice Value
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(invoices.length > 0 
                        ? invoices.reduce((sum, inv) => sum + (inv.InvoicePrice || 0), 0) / invoices.length 
                        : 0
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Peppol Invoices
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {invoices.filter(inv => inv.IsSendOnPeppol).length} / {invoices.length}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </>
          )}
        </TabPanel>

        {/* Payments Tab */}
        <TabPanel value={tabIndex} index={3}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t("payments_tab") || "Payment History"}
          </Typography>
          
          {paymentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <CircularProgress />
            </Box>
          ) : paymentsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {paymentsError}
            </Alert>
          ) : payments.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#f5f5f5' }}>
              <Typography variant="body1" color="text.secondary">
                {t("no_payments_found") || "No payments found for this customer."}
              </Typography>
            </Paper>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("date") || "Date"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("reference") || "Payment Reference"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("document_number") || "Document Number"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("source_type") || "Source Type"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("payment_method") || "Payment Method"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("status") || "Status"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }} align="right">
                        {t("amount") || "Amount"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("notes") || "Notes"}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow 
                        key={payment.Id}
                        sx={{ 
                          '&:hover': { bgcolor: '#f9f9f9' },
                          '&:last-child td, &:last-child th': { border: 0 }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatDate(payment.PaymentDate)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(payment.PaymentDate).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {payment.PaymentReference || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {payment.SourceDocumentNumber || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.SourceType || '-'}
                            size="small"
                            color={getSourceTypeColor(payment.SourceType)}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.PaymentMethod || 'Not Specified'}
                            size="small"
                            color={getPaymentMethodColor(payment.PaymentMethod)}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.PaymentStatus || '-'
                            }
                            size="small"
                            color={payment.PaymentStatus === 'Completed' ? 'success' : payment.PaymentStatus === 'Pending' ? 'warning' : 'default'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 700, 
                              color: 'success.main',
                              fontSize: '0.95rem'
                            }}
                          >
                            {formatCurrency(payment.Amount)}
                          </Typography>
                          {payment.TotalDocumentAmount && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              of {formatCurrency(payment.TotalDocumentAmount)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              maxWidth: 200, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap' 
                            }}
                            title={payment.Notes}
                          >
                            {payment.Notes || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Enhanced Total Summary */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, bgcolor: '#e3f2fd', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t("total_payments") || "Total Payments"}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {formatCurrency(payments.reduce((sum, p) => sum + (p.Amount || 0), 0))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {payments.length} {t("transaction", { count: payments.length }) || `transaction${payments.length !== 1 ? 's' : ''}`}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, bgcolor: '#e8f5e9', borderLeft: '4px solid', borderColor: 'success.main' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t("completed_payments") || "Completed Payments"}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {payments.filter(p => p.PaymentStatus === 'Completed').length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(payments.filter(p => p.PaymentStatus === 'Completed').reduce((sum, p) => sum + (p.Amount || 0), 0))}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, bgcolor: '#fff3e0', borderLeft: '4px solid', borderColor: 'warning.main' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t("pending_payments") || "Pending Payments"}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>
                      {payments.filter(p => p.PaymentStatus === 'Pending').length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(payments.filter(p => p.PaymentStatus === 'Pending').reduce((sum, p) => sum + (p.Amount || 0), 0))}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </TabPanel>

        {/* Credit Notes Tab */}
        <TabPanel value={tabIndex} index={4}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("credit_notes_tab") || "Credit Notes"}
          </Typography>
          <Alert severity="info">
            {t("credit_notes_coming_soon") || "Credit notes content will be displayed here."}
          </Alert>
        </TabPanel>

        {/* Tickets de Caisse Tab */}
        <TabPanel value={tabIndex} index={5}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t("ticket_de_caisse") || "Tickets de Caisse"}
          </Typography>
          
          {ticketsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <CircularProgress />
            </Box>
          ) : ticketsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {ticketsError}
            </Alert>
          ) : tickets.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#f5f5f5' }}>
              <ReceiptIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {t("no_tickets_found") || "No tickets de caisse found for this customer."}
              </Typography>
            </Paper>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("ticket_number") || "Ticket #"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("receipt_number") || "Receipt #"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("date_time") || "Date & Time"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("payment_method") || "Payment Method"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }} align="right">
                        {t("amount") || "Amount"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {t("status") || "Status"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }} align="center">
                        {t("actions") || "Actions"}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tickets.map((ticket) => (
                      <TableRow 
                        key={ticket.Id}
                        sx={{ 
                          '&:hover': { bgcolor: '#f9f9f9' },
                          '&:last-child td, &:last-child th': { border: 0 }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {ticket.TicketNumber || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {ticket.ReceiptNumber || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatDate(ticket.CreatedDate || ticket.Date)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(ticket.CreatedDate || ticket.Date).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ticket.PaymentMethod || 'Cash'}
                            size="small"
                            color={getPaymentMethodColor(ticket.PaymentMethod)}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                            {formatCurrency(ticket.TotalAmount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ticket.Status || 'Completed'}
                            size="small"
                            color={ticket.Status === 'Completed' ? 'success' : ticket.Status === 'Pending' ? 'warning' : 'default'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/tickets/view/${ticket.Id}`)}
                            title={t("view_ticket") || "View Ticket"}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Tickets Summary */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, bgcolor: '#e3f2fd', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t("total_tickets") || "Total Tickets"}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {tickets.length}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, bgcolor: '#e8f5e9', borderLeft: '4px solid', borderColor: 'success.main' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t("total_amount") || "Total Amount"}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {formatCurrency(tickets.reduce((sum, t) => sum + (t.TotalAmount || 0), 0))}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, bgcolor: '#fff3e0', borderLeft: '4px solid', borderColor: 'warning.main' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t("avg_ticket_value") || "Avg Ticket Value"}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>
                      {formatCurrency(tickets.length > 0 ? tickets.reduce((sum, t) => sum + (t.TotalAmount || 0), 0) / tickets.length : 0)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </TabPanel>

        {/* Documents Tab - now index 6 */}
        <TabPanel value={tabIndex} index={6}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("documents_tab") || "Documents"}
          </Typography>
          <Alert severity="info">
            {t("documents_coming_soon") || "Documents content will be displayed here."}
          </Alert>
        </TabPanel>

        {/* Other Tab - now index 7 */}
        <TabPanel value={tabIndex} index={7}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("other_tab") || "Other"}
          </Typography>
          <Alert severity="info">
            {t("other_coming_soon") || "Additional information will be displayed here."}
          </Alert>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default CustomerDetailPage;